import { GridColDef, GridRowsProp } from '@mui/x-data-grid';
import { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../../app-context';
import IGridComponent from '../../../components/grid/i-grid.component';
import TitlePanel from '../../../components/title-panel/title-panel.component';
import { PAGE_SIZE } from '../../common/constants';
import { formatDate, formatDateDay, formatDateTrn, formatTo0Decimal, formatTo2Decimal, isEmpty, isEmptyArray } from '../../common/util.services';
import { getSale, saveSale, saveRate, syncCancellationDate, getOrderCountTrend, getOrderValueTrend, getUnitTrend } from '../amazon.services';
import '../amazon.scss';
import IDialogComponent from '../../../components/dialog/i-dialog.component';
import EditCellComponent from '../../costing/detail/edit-cell.component';
import { useNavigate } from 'react-router-dom';
import { Stack } from '@mui/system';
import {AssessmentOutlined, PlayCircleOutline} from '@mui/icons-material';
import YesNoDialog from '../../../components/message-dialog/yes-no-dialog.component';
import LineChart from './line-chart.component';

const AmazonDailyOrder = () => {
  const appContext = useContext(AppContext);
  const rows: GridRowsProp = [];
  const [saleData, setSaleData] = useState(rows)
  const [openSale, setOpenSale] = useState(false);
  const [openChart, setOpenChart] = useState(false);
  const [cellLabel, setCellLabel] = useState('');
  const [title, setTitle] = useState('');
  const [orderDate, setOrderDate] = useState('');
  const [showSyncMsg, setShowSyncMsg] = useState(false);
  const [orderCountTrendData, setOrderCountTrendData] = useState<any>([]);
  const [orderValueTrendData, setOrderValueTrendData] = useState<any>([]);
  const [cumulativeTrendData, setCumulativeTrendData] = useState<any>([]);
  const [asinUnitTrendData, setAsinUnitTrendData] = useState<any>([]);
  const [totalUnitTrendData, setTotalUnitTrendData] = useState<any>([]);
  const [cellValueRow, setCellValueRow] = useState<any>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [totalRows, setTotalRows] = useState(0);
  const navigate = useNavigate();
  const columns: GridColDef[] = [
    { field: 'orderDate', headerName: 'Date', filterable: false, sortable: false, width: 180,
      valueFormatter: (value: any) => formatDateDay(value)
    },
    { field: 'usaSale', headerName: 'USA Order', width: 160, filterable: false, sortable: false,
      valueFormatter: (value: any) => getValue0Decimal(value)
    },
    { field: 'canadaSale', headerName: 'Canada Order', width: 160, filterable: false, sortable: false,
      valueFormatter: (value: any) => getValue0Decimal(value)
    },
    { field: 'mexicoSale', headerName: 'Mexico Order', width: 160, filterable: false, sortable: false,
      valueFormatter: (value: any) => getValue0Decimal(value),
    },
    { field: 'nonAmazonSale', headerName: 'Non-Amz Ord', width: 160, filterable: false, sortable: false,
      valueFormatter: (value: any) => getValue0Decimal(value)
    },
    { field: 'totalSale', headerName: 'Total Order', width: 150, filterable: false, sortable: false,
      renderCell: (params) => (<Stack direction="row" alignItems="center" gap={1}>
                               <AssessmentOutlined fontSize='small' color="primary"/>
                               {getValue0Decimal(params.row.totalSale)} </Stack>)
    },
    { field: 'totalPrice', headerName: 'Total Inv Price', width: 150, filterable: false, sortable: false,
      valueFormatter: (value: any) => getValue0Decimal(value)
    },
    { field: 'discount', headerName: 'Discount', width: 150, filterable: false, sortable: false,
      cellClassName: (params) => { return params.row.discount > 0.001 ? 'AdDiffN' : 'AdDiffP'},
      valueFormatter: (value: any) => formatTo2Decimal(value) + '%'
    },
    { field: 'syncCancel', headerName: 'Cancel', width: 60, filterable: false, sortable: false,
      renderCell: (params) => (<Stack direction="row" alignItems="center" justifyContent="center" sx={{ height: '100%' }}>
                               <PlayCircleOutline fontSize='small' color="secondary"/> </Stack>)
    }
  ];

  const closeSale = (value: any) => {
    if('Ok' === value) {
      setLoading(true);
      appContext.setShowLoader(true);
      saveSale(cellValueRow, appContext,
        (response: any) => {
          appContext.setSuccessMessage(response.message)
          appContext.setShowLoader(false);
          setLoading(false);
          setOpenSale(false);
        }, (error: any) => {
          appContext.setShowLoader(false);
          appContext.setErrorMessage(error.message)
          setLoading(false);
          setOpenSale(false);
      });
    } else {
      setOpenSale(false);
    }
  }

  const onCellDoubleClick = (value: any) => {
    const field = value.field;
    if('canadaSale' === field) {
      setCellValueRow(value.row);
      setCellLabel('Canada Sale');
      setTitle('Specify Canada Sale');
      //setOpenSale(true);
    } else if('mexicoSale' === field) {
      setCellValueRow(value.row);
      setCellLabel('Mexico Sale');
      setTitle('Specify Mexico Sale');
      //setOpenSale(true);
    }
  }

  const onCellClick = (value: any) => {
    setOrderDate(value.row.date);
    if('syncCancel' === value.field) {
      setShowSyncMsg(true);
    } else if('totalSale' === value.field) {
      openOrderChart(value.row.orderDate);
    }
  }

  const onOkClick = () => {
    setLoading(true);
    syncCancellationDate(orderDate, appContext,
      (response: any) => {
        appContext.setShowLoader(false);
        setLoading(false);
      }, (error: any) => {
        console.log(error);
        appContext.setShowLoader(false);
        appContext.setErrorMessage(error.message);
        setLoading(false);
      });
  }

  function getSaleData() {
    if (isEmptyArray(rows)) {
      setLoading(true);
      getSale(page, pageSize, appContext,
        (response: any) => {
          setSaleData(response.data.content);
          setTotalRows(response.data.totalElements);
          appContext.setShowLoader(false);
          setLoading(false);
        }, (error: any) => {
          console.log(error);
          appContext.setShowLoader(false);
          appContext.setErrorMessage(error.message);
          setLoading(false);
        });
    }
  }

  useEffect(() => {
    getSaleData();
  }, [page, pageSize]);

  const onPaginationModelChange = (evt: any) => {
    setPage(evt.page);
    setPageSize(evt.pageSize);
  }

  const getValue0Decimal = (value: any) => {
    return isEmpty(value) ? '0' : formatTo0Decimal(Math.round(value * 1));
  }

  const closeChart = (value: any) => {
    setOpenChart(false);
  }

  const openOrderChart = (value: any) => {
    let date = isEmpty(value) ? 'ALL' : formatDateTrn(value);
    setOrderDate(date);
    setLoading(true);
    getOrderCountTrend(date, appContext,
      (response: any) => {
        setOrderCountTrendData(response.data);
        appContext.setShowLoader(false);
        setLoading(false);
        setOpenChart(true);
      }, (error: any) => {
        console.log(error);
        appContext.setShowLoader(false);
        appContext.setErrorMessage(error.message);
        setLoading(false);
      });
    
    getOrderValueTrend(date, 'Value', appContext,
      (response: any) => {
        setOrderValueTrendData(response.data);
        appContext.setShowLoader(false);
        setLoading(false);
        setOpenChart(true);
      }, (error: any) => {
        console.log(error);
        appContext.setShowLoader(false);
        appContext.setErrorMessage(error.message);
        setLoading(false);
      });

    getOrderValueTrend(date, 'Cumulative', appContext,
      (response: any) => {
        setCumulativeTrendData(response.data);
        appContext.setShowLoader(false);
        setLoading(false);
        setOpenChart(true);
      }, (error: any) => {
        console.log(error);
        appContext.setShowLoader(false);
        appContext.setErrorMessage(error.message);
        setLoading(false);
      });

    getUnitTrend(date, 'Asin', appContext,
      (response: any) => {
        setAsinUnitTrendData(response.data);
        appContext.setShowLoader(false);
        setLoading(false);
        setOpenChart(true);
      }, (error: any) => {
        console.log(error);
        appContext.setShowLoader(false);
        appContext.setErrorMessage(error.message);
        setLoading(false);
      });

    getUnitTrend(date, 'Total', appContext,
      (response: any) => {
        setTotalUnitTrendData(response.data);
        appContext.setShowLoader(false);
        setLoading(false);
        setOpenChart(true);
      }, (error: any) => {
        console.log(error);
        appContext.setShowLoader(false);
        appContext.setErrorMessage(error.message);
        setLoading(false);
      });
  }

  const processRowUpdate = (updatedRow: any, originalRow: any) => {
    if(updatedRow.canadaRate === originalRow.canadaRate && updatedRow.mexicoRate === originalRow.mexicoRate) {
      return;
    }
    setLoading(true);
    appContext.setShowLoader(true);
    saveRate(updatedRow, appContext,
      (response: any) => {
        appContext.setSuccessMessage(response.message)
        navigate(0);
        appContext.setShowLoader(false);
        setLoading(false);
      }, (error: any) => {
        appContext.setShowLoader(false);
        appContext.setErrorMessage(error.message)
        setLoading(false);
    });
  }

  const getMainPanel = () => {
    return <>
      <IGridComponent title={''} rows={saleData} columns={columns} loading={loading} maxHeight={450}
        paginationType="server" paginationModelChange={onPaginationModelChange} totalRows={totalRows} 
        processRowUpdate={processRowUpdate} cellClick={onCellClick} 
        showExtBtn={false} extBtnText={'Trend'} extBtnAction={openOrderChart}
        getRowId={(row: any) => row.rowId} showAction={false} cellDoubleClick={onCellDoubleClick} />
      <YesNoDialog open={showSyncMsg} onClose={() => { setShowSyncMsg(false) }}
        onOk={() => { setShowSyncMsg(false); onOkClick(); }} isHorizontal={true} 
        message={'Do you want to sync the data?'}/>
      <IDialogComponent onClose={closeSale} open={openSale} title={title}
        dialogBody={<EditCellComponent data={cellValueRow} close={closeSale} label={cellLabel}/>} />
      <IDialogComponent onClose={closeChart} open={openChart} title={'Order Trend: ' + ('ALL' === orderDate ? 'ALL' : formatDate(orderDate))}
        dialogBody={<LineChart data1={orderCountTrendData} data2={orderValueTrendData} 
        data3={asinUnitTrendData} data4={totalUnitTrendData} data5={cumulativeTrendData} orderDate={orderDate}/>} />
    </>
  }

  return <TitlePanel title='Daily Order' panelBody={getMainPanel()} />
}

export default AmazonDailyOrder;